<template>
  <div class="space-y-6">
    <!-- Search Header -->
    <div class="text-center">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-4">Search Documentation</h1>
      <p class="text-lg text-gray-600 dark:text-gray-300 mb-8">
        Find exactly what you're looking for with our powerful search capabilities
      </p>
    </div>

    <!-- Search Form -->
    <div class="max-w-2xl mx-auto">
      <div class="relative">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
        </div>
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-800 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 text-lg transition-colors duration-200"
          placeholder="Enter your search query..."
          @input="handleSearch"
          @keydown.enter="performSearch"
        />
        <button
          @click="performSearch"
          class="absolute inset-y-0 right-0 px-4 bg-primary-600 dark:bg-primary-500 text-white rounded-r-lg hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 transition-colors duration-200"
        >
          Search
        </button>
      </div>
    </div>

    <!-- Search Filters -->
    <div
      v-if="hasActiveFilters || searchResults.length > 0"
      class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
    >
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-50">Filters</h3>
        <button
          @click="clearFilters"
          class="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors duration-200"
        >
          Clear All
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <!-- Category Filter -->
        <div>
          <label class="form-label">Categories</label>
          <select
            v-model="filters.categories"
            multiple
            class="form-input h-auto min-h-[80px]"
            size="4"
          >
            <option
              v-for="category in availableCategories"
              :key="category.slug"
              :value="category.slug"
            >
              {{ category.title }}
            </option>
          </select>
        </div>

        <!-- Difficulty Filter -->
        <div>
          <label class="form-label">Difficulty</label>
          <div class="space-y-2">
            <label
              v-for="difficulty in ['beginner', 'intermediate', 'advanced']"
              :key="difficulty"
              class="flex items-center"
            >
              <input
                v-model="filters.difficulty"
                type="checkbox"
                :value="difficulty"
                class="rounded border-gray-300 dark:border-gray-600 text-primary-600 dark:text-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400 bg-white dark:bg-gray-700"
              />
              <span class="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">{{ difficulty }}</span>
            </label>
          </div>
        </div>

        <!-- Date Range Filter -->
        <div>
          <label class="form-label">Date Range</label>
          <div class="space-y-2">
            <input
              v-model="startDate"
              type="date"
              class="form-input text-sm"
              placeholder="Start date"
            />
            <input
              v-model="endDate"
              type="date"
              class="form-input text-sm"
              placeholder="End date"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Search Results -->
    <div v-if="searchResults.length > 0" class="space-y-4">
      <div class="flex items-center justify-between">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-50">
          {{ searchResults.length }} result{{ searchResults.length === 1 ? '' : 's' }}
          <span v-if="searchQuery" class="text-gray-500 dark:text-gray-400 font-normal">
            for "{{ searchQuery }}"
          </span>
        </h2>

        <div class="flex items-center space-x-2">
          <label class="text-sm text-gray-600 dark:text-gray-400">Sort by:</label>
          <select v-model="sortBy" class="form-input text-sm py-1" @change="sortResults">
            <option value="relevance">Relevance</option>
            <option value="date">Date</option>
            <option value="title">Title</option>
          </select>
        </div>
      </div>

      <!-- Results List -->
      <div class="space-y-4">
        <div
          v-for="result in sortedResults"
          :key="result.id"
          class="card hover:shadow-md transition-shadow duration-200 cursor-pointer"
          @click="navigateToResult(result)"
        >
          <div class="card-body">
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1">
                <div class="flex items-center space-x-2 mb-2">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-50">{{ result.title }}</h3>
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  >
                    {{ result.category }}
                  </span>
                  <DifficultyBadge v-if="result.difficulty" :difficulty="result.difficulty" />
                </div>

                <!-- Highlighted Content -->
                <div class="text-gray-600 dark:text-gray-300 mb-3 line-clamp-3">
                  <span v-if="result.matches.title" class="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
                    {{ result.title }}
                  </span>
                  <span v-else>{{ result.title }}</span>
                  -
                  <span v-if="result.matches.content" class="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
                    {{ getContentPreview(result.content) }}
                  </span>
                  <span v-else>{{ getContentPreview(result.content) }}</span>
                </div>

                <!-- Tags -->
                <div v-if="result.tags.length > 0" class="flex flex-wrap gap-1 mb-3">
                  <span
                    v-for="tag in result.tags.slice(0, 5)"
                    :key="tag"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                  >
                    {{ tag }}
                  </span>
                  <span v-if="result.tags.length > 5" class="text-xs text-gray-500 dark:text-gray-400">
                    +{{ result.tags.length - 5 }} more
                  </span>
                </div>

                <!-- Metadata -->
                <div class="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                  <span>Updated {{ formatDate(result.lastUpdated) }}</span>
                  <span class="flex items-center">
                    <div class="flex items-center space-x-1 mr-2">
                      <div
                        v-for="i in 3"
                        :key="i"
                        class="w-2 h-2 rounded-full"
                        :class="
                          i <= getRelevanceScore(result.score) ? 'bg-primary-500 dark:bg-primary-400' : 'bg-gray-200 dark:bg-gray-600'
                        "
                      ></div>
                    </div>
                    Relevance: {{ result.score }}
                  </span>
                </div>
              </div>

              <ChevronRightIcon class="w-5 h-5 text-gray-400 dark:text-gray-500 ml-4 flex-shrink-0" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Results -->
    <div v-else-if="searchQuery && !isLoading" class="text-center py-12">
      <MagnifyingGlassIcon class="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
      <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">No Results Found</h2>
      <p class="text-gray-600 dark:text-gray-300 mb-6">
        We couldn't find any documentation matching "{{ searchQuery }}". Try adjusting your search
        terms or filters.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <button @click="clearSearch" class="btn-outline">Clear Search</button>
        <button @click="browseAll" class="btn-primary">Browse All Documentation</button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-12">
      <div
        class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400 mx-auto mb-4"
      ></div>
      <p class="text-gray-600 dark:text-gray-400">Searching documentation...</p>
    </div>

    <!-- Search Suggestions -->
    <div
      v-if="searchQuery && searchResults.length === 0 && !isLoading"
      class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6"
    >
      <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">Search Suggestions</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="space-y-2">
          <p class="text-sm text-gray-600 dark:text-gray-400">Try these tips:</p>
          <ul class="text-sm text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
            <li>Use shorter, more general terms</li>
            <li>Check your spelling</li>
            <li>Try different keywords</li>
            <li>Use filters to narrow results</li>
          </ul>
        </div>
        <div class="space-y-2">
          <p class="text-sm text-gray-600 dark:text-gray-400">Popular searches:</p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="suggestion in searchSuggestions"
              :key="suggestion"
              @click="handleSuggestionClick(suggestion)"
              class="px-3 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full text-sm text-gray-700 dark:text-gray-300 hover:border-primary-300 dark:hover:border-primary-500 hover:text-primary-700 dark:hover:text-primary-400 transition-colors duration-200"
            >
              {{ suggestion }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import { MagnifyingGlassIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
  import { useDocumentationStore } from '@/stores/documentation'
  import DifficultyBadge from '@/components/ui/DifficultyBadge.vue'
  import type { SearchResult, SearchFilters } from '@/types/documentation'

  // Router
  const router = useRouter()

  // Store
  const documentationStore = useDocumentationStore()

  // Refs
  const searchInput = ref<HTMLInputElement>()
  const searchQuery = ref('')
  const sortBy = ref<'relevance' | 'date' | 'title'>('relevance')
  const filters = ref<SearchFilters>({
    categories: [],
    tags: [],
    difficulty: [],
    dateRange: {
      start: new Date(),
      end: new Date(),
    },
  })

  // Computed
  const searchResults = computed(() => documentationStore.searchResults)
  const isLoading = computed(() => documentationStore.isLoading)

  const availableCategories = computed(() => documentationStore.documentation)

  // Computed properties for date inputs
  const startDate = computed({
    get: () => filters.value.dateRange?.start?.toISOString().split('T')[0] || '',
    set: (value: string) => {
      if (filters.value.dateRange) {
        filters.value.dateRange.start = new Date(value)
      }
    }
  })

  const endDate = computed({
    get: () => filters.value.dateRange?.end?.toISOString().split('T')[0] || '',
    set: (value: string) => {
      if (filters.value.dateRange) {
        filters.value.dateRange.end = new Date(value)
      }
    }
  })

  const hasActiveFilters = computed(() => {
    return (
      filters.value.categories.length > 0 ||
      (filters.value.difficulty && filters.value.difficulty.length > 0) ||
      (filters.value.dateRange && filters.value.dateRange.start) ||
      (filters.value.dateRange && filters.value.dateRange.end)
    )
  })

  const sortedResults = computed(() => {
    const results = [...searchResults.value]

    switch (sortBy.value) {
      case 'date':
        return results.sort(
          (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
        )
      case 'title':
        return results.sort((a, b) => a.title.localeCompare(b.title))
      default:
        return results.sort((a, b) => b.score - a.score)
    }
  })

  const searchSuggestions = computed(() => [
    'API',
    'Authentication',
    'Configuration',
    'Deployment',
    'Troubleshooting',
  ])

  // Methods
  const handleSearch = () => {
    if (searchQuery.value.trim()) {
      performSearch()
    }
  }

  const performSearch = () => {
    if (searchQuery.value.trim()) {
      documentationStore.searchDocumentation(searchQuery.value)
    }
  }

  const clearSearch = () => {
    searchQuery.value = ''
    documentationStore.clearSearch()
  }

  const clearFilters = () => {
    filters.value = {
      categories: [],
      tags: [],
      difficulty: [],
      dateRange: {
        start: new Date(),
        end: new Date(),
      },
    }
  }

  const sortResults = () => {
    // Results are automatically sorted by computed property
  }

  const navigateToResult = (result: SearchResult) => {
    router.push(`/docs/${result.categorySlug}/${result.sectionSlug}`)
  }

  const browseAll = () => {
    if (documentationStore.documentation.length > 0) {
      router.push(`/docs/${documentationStore.documentation[0].slug}`)
    }
  }

  const getContentPreview = (content: string): string => {
    const maxLength = 150
    if (content.length <= maxLength) return content
    return `${content.substring(0, maxLength)  }...`
  }

  const getRelevanceScore = (score: number): number => {
    if (score >= 5) return 3
    if (score >= 3) return 2
    return 1
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

    if (diffInDays === 0) return 'today'
    if (diffInDays === 1) return 'yesterday'
    if (diffInDays < 7) return `${diffInDays} days ago`
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`
    return `${Math.floor(diffInDays / 365)} years ago`
  }

  const handleSuggestionClick = (suggestion: string) => {
    searchQuery.value = suggestion
    performSearch()
  }

  // Watch for filter changes
  watch(
    filters,
    () => {
      if (searchQuery.value.trim()) {
        performSearch()
      }
    },
    { deep: true }
  )

  // Lifecycle
  onMounted(() => {
    // Focus search input
    searchInput.value?.focus()

    // Load search query from store if exists
    if (documentationStore.searchQuery) {
      searchQuery.value = documentationStore.searchQuery
    }
  })
</script>

<style scoped>
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Smooth transitions */
  .transition-all {
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 200ms;
  }
</style>
